import { useState, useEffect, useRef } from "react";

import { ImpressionsChart } from "../../components/charts3";
import { ChartControls } from "../../components/Channel";

import { useDaysSorted } from "../../hooks/useDaysSorted";
import { useDateInputs } from "../../hooks/useDateInputs";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";

import { useTotalViewersSeries } from "../../hooks/useTotalViewersSeries";

/**
 * Total Viewers TR allowed modes
 */
const TOTAL_VIEWERS_MODES = ["minute", "hour", "day", "week", "month"];

export default function ChannelTotalViewersTr({ channelId, rawImpressions, loading, refreshKey, refreshEnabled, setRefreshEnabled, refreshIntervalMin, setRefreshIntervalMin, refreshCountdown, manualRefresh }) {
  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  const defaultMode = storedUser.chartdefault || "minute";
  
  const [mode, setMode] = useState(defaultMode);

  // --- LOGGER INTEGRATION ---
  const { logPageChange, logTimeframeChange } = useUserJourneyLogger({
    channelId,
    // channelName is not available here, so omit or set to undefined
  });

  useEffect(() => {
    logPageChange({
      chartType: "totalviewerstr",
    });
  }, [logPageChange]);

  // Only log timeframe change on user-initiated mode change
  const handleModeChange = (newMode) => {
    setMode(newMode);
    logTimeframeChange({
      chartType: "totalviewerstr",
      mode: newMode,
    });
  };

  // Time range (minute only – harmless for others)
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  // rawImpressions and loading are now received as props
  const sortedKeys = useDaysSorted(rawImpressions);

  // ✅ SINGLE source of truth for dates (YYYY-MM-DD)
  const dates = useDateInputs(sortedKeys, mode);

  /**
   * ✅ Minute mode default date
   * - Only once per entry into minute mode
   * - Never override user selection
   */
  const minuteInitRef = useRef(false);

  useEffect(() => {
    if (mode !== "minute") return;
    if (!sortedKeys?.length) return;
    if (minuteInitRef.current) return;

    if (dates.startDateInput && dates.endDateInput) {
      minuteInitRef.current = true;
      return;
    }

    const latestKey = sortedKeys.at(-1); // DD-MM-YYYY
    const [d, m, y] = latestKey.split("-");
    const uiDate = `${y}-${m}-${d}`; // YYYY-MM-DD

    dates.setStartDateInput(uiDate);
    dates.setEndDateInput(uiDate);

    minuteInitRef.current = true;
  }, [mode, sortedKeys, dates]);

  // Reset minute init when leaving minute
  useEffect(() => {
    if (mode !== "minute") {
      minuteInitRef.current = false;
    }
  }, [mode]);

  /**
   * 🔑 NORMALIZED SERIES
   * - If data exists, return array (possibly empty)
   * - Empty array must NOT block render
   */
  const series = useTotalViewersSeries({
    rawImpressions,
    sortedImpKeys: sortedKeys,
    startDateInput: dates.startDateInput,
    endDateInput: dates.endDateInput,
    mode,
    startTime,
    endTime,
  });

  /**
   * 🔑 RENDER RULE
   * - loading blocks
   * - AFTER loading → always render chart
   */
  const showChart = !loading;

  return (
    <>
      <ChartControls
        channelId={channelId}
        mode={mode}
        onModeChange={handleModeChange}
        allowedModes={TOTAL_VIEWERS_MODES}
        startDateInput={dates.startDateInput}
        endDateInput={dates.endDateInput}
        onStartDateChange={dates.setStartDateInput}
        onEndDateChange={dates.setEndDateInput}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      {showChart ? (
        <ImpressionsChart 
        refreshKey={refreshKey}
          series={series} 
          mode={mode}
          startTime={startTime}
          endTime={endTime}
          title="Total Viewers Türkiye"
        />
      ) : (
        <div className="chart-container">
          <h3 className="chart-title">Total Viewers Türkiye</h3>
          <div className="chart-loading">
            <div className="chart-skeleton" />
            <div className="chart-loading-text">Loading chart...</div>
          </div>
        </div>
      )}
    </>
  );
}
